import { TextField, Box, styled, InputAdornment, useTheme } from '@mui/material';
import { boStyle } from 'constants/content.constants';
import { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import _ from 'lodash';

const onSwitch = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="4" width="8" viewBox="1 2 7 7"><path fill="${encodeURIComponent(
	'none'
)}" stroke="${encodeURIComponent('#fff')}" d="M1 4.33333L3.54545 7L8 1"/></svg>')`;

const offSwitch = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="14" width="12" viewBox="0 0 18 16"><path fill="${encodeURIComponent(
	'none'
)}" stroke="${encodeURIComponent(
	'#fff'
)}" stroke-linecap="round" stroke-linejoin="round" d="M7.36678 6.1038L10.9188 9.65578"/><path fill="${encodeURIComponent(
	'none'
)}" stroke="${encodeURIComponent(
	'#fff'
)}" stroke-linecap="round" stroke-linejoin="round" d="M7.36678 9.52413L10.9188 5.97215"/></svg>')`;

interface IProps {
	value: string;
	label?: string;
	name?: string;
	onChange: any;
	inputstyle?: string;
	standard?: boolean;
	textFieldStyle?: any;
	placeholder?: string;
	icon?: any;
	disabled?: boolean;
	toolbarOptions?: Record<string, any>;
	textAlignment?: 'left' | 'right' | 'center';
}

interface ICustomTextFieldProps {
	inputstyle?: string;
	icon?: any;
	theme?: any;
}

interface ICustomSwitchInputProps {
	children: any;
	checked?: boolean;
	theme: any;
	styleObj?: any;
}

export const CustomTextField = styled(Box)(({ inputstyle, icon, theme }: ICustomTextFieldProps) => ({
	'& .MuiTextField-root': {
		'& .MuiInputBase-input': {
			height: inputstyle === boStyle.TEXT_AREA ? '100px!important' : 'auto',
			overflowY: inputstyle === boStyle.TEXT_AREA ? 'auto!important' : 'hidden',
			padding: '10px',
			paddingLeft: icon ? '30px' : 'auto',
		},
		'& .MuiOutlinedInput-root': {
			borderRadius: '4px',
			padding: 0,
		},
		'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
			border: `1px solid ${theme.palette.root.inputFocusGreen}`,
		},
	},
}));
export const TextInput = ({
	value,
	label,
	onChange,
	inputstyle,
	standard,
	textFieldStyle,
	placeholder,
	icon,
	disabled,
	toolbarOptions,
	textAlignment,
}: IProps) => {
	const theme = useTheme();
	const [newValue, setNewValue] = useState(value);

	const [editorState, setEditorState] = useState<any>(() => {
		if (inputstyle === boStyle.RICH_TEXT) {
			const blocksFromHtml = htmlToDraft(value);
			const { contentBlocks, entityMap } = blocksFromHtml;
			const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
			const editorState = EditorState.createWithContent(contentState);
			return editorState;
		}
	});

	useEffect(() => {
		setNewValue(value);

		if (!_.isEqual(value, newValue) && inputstyle === boStyle.RICH_TEXT) {
			const blocksFromHtml = htmlToDraft(value);
			const { contentBlocks, entityMap } = blocksFromHtml;
			const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
			const editorState = EditorState.createWithContent(contentState);
			setEditorState(editorState);
		}
	}, [value]);

	if (inputstyle === boStyle.RICH_TEXT) {
		return (
			<>
				<Editor
					editorState={editorState}
					toolbar={toolbarOptions}
					placeholder={placeholder}
					textAlignment={textAlignment}
					toolbarClassName="toolbarClassName"
					wrapperClassName="wrapperClassName"
					editorClassName="editorClassName"
					editorStyle={{
						border: '1px solid rgba(0, 0, 0, 0.23)',
						borderRadius: '2px',
						height: '400px',
						overflowY: 'auto',
						wordBreak: 'break-word',
						padding: '10px',
					}}
					onEditorStateChange={(state: any) => {
						const newValHtml = draftToHtml(convertToRaw(state.getCurrentContent()));
						setNewValue(newValHtml);
						setEditorState(state);
						onChange(newValHtml);
					}}
					style={{ marginTop: '10px' }}
				/>
				<textarea hidden disabled value={newValue}></textarea>
			</>
		);
	}
	return (
		<CustomTextField inputstyle={inputstyle} icon={icon} theme={theme}>
			<TextField
				id="standard-basic"
				variant={standard ? 'standard' : 'outlined'}
				type="text"
				disabled={disabled}
				label={label}
				value={newValue}
				multiline={inputstyle === boStyle.TEXT_AREA}
				onChange={(event: any) => {
					setNewValue(event?.target.value);
					onChange(event?.target.value);
				}}
				sx={{
					width: '100%',
					...textFieldStyle,
					'& .MuiStack-root': {
						position: 'relative',
					},
				}}
				placeholder={placeholder || ''}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start" sx={{ position: 'absolute' }}>
							{icon}
						</InputAdornment>
					),
				}}
			/>
		</CustomTextField>
	);
};

export const PasswordInput = ({ value, label, onChange }: IProps) => {
	return (
		<TextField
			id="standard-basic"
			variant="standard"
			type="password"
			autoComplete="new-password"
			label={label}
			value={value}
			onChange={onChange}
			sx={{ width: '100%' }}
		/>
	);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CustomSwitch = styled('div')(({ children, checked, theme, styleObj }: ICustomSwitchInputProps) => ({
	'& .MuiSwitch-root': {
		padding: 0,
		borderRadius: '16px',
		width: '52px',
		height: '25px',
		'& .MuiSwitch-track': {
			background: theme.palette.root.lightOrange_2,
		},
		'& .Mui-checked+.MuiSwitch-track': {
			background: theme.palette.root.lightTeal,
		},
		'& .Mui-disabled+.MuiSwitch-track': {
			background: checked ? theme.palette.root.forestGreen : theme.palette.root.orange_3,
		},
		'& .MuiButtonBase-root': {
			transform: checked ? 'translateX(27px)' : 'auto',
		},
		'& .MuiSwitch-thumb': {
			background: checked ? theme.palette.root.forestGreen : theme.palette.root.orange_3,
			boxShadow: 'none',
			width: 8,
			height: 8,
			transform: 'scale(2.2)',
			backgroundRepeat: 'no-repeat',
			backgroundPosition: 'center',
			backgroundImage: checked ? onSwitch : offSwitch,
		},
		...styleObj,
	},
}));
